import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import {
  User as FirebaseUser,
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  getIdTokenResult,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, Firestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface User {
  uid: string;
  name: string; // Deprecated: use firstName + lastName
  firstName?: string;
  lastName?: string;
  email: string;
  stripeCustomerId?: string;
  futureMeetingsOptIn: boolean;
  units: any[];
  createdAt: Date;
  lastLoginAt: Date;
  twoFactorEnabled: boolean;
  twoFactorSecret?: string;
  twoFactorBackupCodes?: string[];
  rank?: string;
  unitName?: string;
  unitNumber?: string;
  motherUnitName?: string;
  motherUnitNumber?: string;
  profileComplete?: boolean;
  isAdmin?: boolean;
  hasSeenWelcome?: boolean;
  emailVerified?: boolean;
  trustedDevices?: any[];
  pendingEmailChange?: any;
  [key: string]: any; // Allow additional fields
}

export interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  signup: (email: string, password: string, additionalData: Record<string, any>) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUserProfile: () => Promise<boolean>;
  sendVerificationEmail: (redirectPath?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export interface AuthProviderProps {
  children: React.ReactNode;
  auth: Auth;
  db: Firestore;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export function AuthProvider({
  children,
  auth,
  db,
  onSuccess,
  onError,
}: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshMutexRef = useRef<Promise<boolean> | null>(null);
  const membershipClaimUidRef = useRef<string | null>(null);

  /**
   * Once the account email is verified, link any email-placeholder unit memberships an officer
   * created before this account existed. Runs once per account (the callable sets
   * emailMembershipsClaimedAt); needs a verified email so nobody can claim someone else's.
   * Returns true when the profile should be re-read.
   */
  const claimEmailMembershipsOnce = useCallback(
    async (uid: string, profileData: Record<string, unknown>): Promise<boolean> => {
      const user = auth.currentUser;
      if (
        !user ||
        user.uid !== uid ||
        !user.emailVerified ||
        profileData.emailMembershipsClaimedAt ||
        membershipClaimUidRef.current === uid
      ) {
        return false;
      }
      membershipClaimUidRef.current = uid;
      try {
        // Make sure the ID token carries email_verified after a fresh verification.
        await user.getIdToken(true);
        await httpsCallable(getFunctions(auth.app, 'europe-west2'), 'claimEmailUnitMemberships')();
        return true;
      } catch (error) {
        console.error('Error linking email-based unit memberships:', error);
        return false;
      }
    },
    [auth],
  );

  const fetchUserProfile = useCallback(async (uid: string): Promise<boolean> => {
    try {
      let userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists() && (await claimEmailMembershipsOnce(uid, userDoc.data()))) {
        userDoc = await getDoc(doc(db, 'users', uid));
      }
      if (userDoc.exists()) {
        const data = userDoc.data();
        const docEmailVerified = !!data.emailVerified;
        
        // Check if any array fields are null and need to be cleaned up
        const needsCleanup = 
          data.units === null || 
          data.guestSubscriptions === null || 
          data.trustedDevices === null || 
          data.twoFactorBackupCodes === null ||
          data.dismissedUnitApplications === null;
        
        // If null array fields exist, clean them up in Firestore
        if (needsCleanup) {
          const cleanupData: any = {};
          if (data.units === null) cleanupData.units = [];
          if (data.guestSubscriptions === null) cleanupData.guestSubscriptions = [];
          if (data.trustedDevices === null) cleanupData.trustedDevices = [];
          if (data.twoFactorBackupCodes === null) cleanupData.twoFactorBackupCodes = [];
          if (data.dismissedUnitApplications === null) cleanupData.dismissedUnitApplications = [];
          
          // Update Firestore to fix null values
          try {
            await updateDoc(doc(db, 'users', uid), cleanupData);
            console.log('✅ Cleaned up null array fields in user profile');
          } catch (cleanupError) {
            console.error('Error cleaning up null fields:', cleanupError);
          }
        }
        
        setUserProfile({
          uid: userDoc.id,
          name: data.name || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : ''),
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          stripeCustomerId: data.stripeCustomerId,
          futureMeetingsOptIn: data.futureMeetingsOptIn || false,
          units: data.units || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
          twoFactorEnabled: data.twoFactorEnabled || false,
          twoFactorSecret: data.twoFactorSecret,
          twoFactorBackupCodes: data.twoFactorBackupCodes || [],
          rank: data.rank,
          unitName: data.unitName,
          unitNumber: data.unitNumber,
          motherUnitName: data.motherUnitName,
          motherUnitNumber: data.motherUnitNumber,
          profileComplete: data.profileComplete || false,
          isAdmin: data.isAdmin || false,
          hasSeenWelcome: data.hasSeenWelcome || false,
          emailVerified: data.emailVerified || false,
          guestSubscriptions: data.guestSubscriptions?.map((g: any) => ({
            unitId: g.unitId,
            subscribedAt: g.subscribedAt?.toDate() || new Date(),
          })) || [],
          trustedDevices: data.trustedDevices?.map((d: any) => ({
            id: d.id,
            name: d.name,
            userAgent: d.userAgent,
            ipAddress: d.ipAddress,
            lastUsed: d.lastUsed?.toDate() || new Date(),
            createdAt: d.createdAt?.toDate() || new Date(),
          })) || [],
          dismissedUnitApplications: data.dismissedUnitApplications || [],
          pendingEmailChange: data.pendingEmailChange ? {
            newEmail: data.pendingEmailChange.newEmail,
            currentEmailToken: data.pendingEmailChange.currentEmailToken,
            newEmailToken: data.pendingEmailChange.newEmailToken,
            currentEmailVerified: data.pendingEmailChange.currentEmailVerified || false,
            newEmailVerified: data.pendingEmailChange.newEmailVerified || false,
            initiatedAt: data.pendingEmailChange.initiatedAt?.toDate() || new Date(),
            expiresAt: data.pendingEmailChange.expiresAt?.toDate() || new Date(),
          } : undefined,
          ...Object.keys(data).reduce((acc, key) => {
            if (!['name', 'firstName', 'lastName', 'email', 'stripeCustomerId', 'futureMeetingsOptIn', 'units', 'createdAt', 'lastLoginAt', 'twoFactorEnabled', 'twoFactorSecret', 'twoFactorBackupCodes', 'rank', 'unitName', 'unitNumber', 'motherUnitName', 'motherUnitNumber', 'profileComplete', 'isAdmin', 'hasSeenWelcome', 'emailVerified', 'guestSubscriptions', 'trustedDevices', 'dismissedUnitApplications', 'pendingEmailChange'].includes(key)) {
              acc[key] = data[key];
            }
            return acc;
          }, {} as Record<string, any>),
        } as User);
        return docEmailVerified;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
    return false;
  }, [db, claimEmailMembershipsOnce]);

  /**
   * Syncs Auth + Firestore profile. Prefers ID token claims (secure token endpoint) and only calls
   * User.reload() (Identity Toolkit account lookup) when the token says the email is verified but
   * the in-memory User is stale. Avoids hammering reload() on a timer while the user is still
   * unverified, which was a common source of auth/network-request-failed on slow mobile networks.
   */
  const refreshUserProfile = useCallback(async (): Promise<boolean> => {
    if (!currentUser) {
      return false;
    }

    const run = async (): Promise<boolean> => {
      try {
        try {
          const idTokenResult = await getIdTokenResult(currentUser, true);
          const claims = idTokenResult.claims as Record<string, unknown>;
          const verifiedInToken = claims['email_verified'] === true;
          if (verifiedInToken && !currentUser.emailVerified) {
            await currentUser.reload();
          }
        } catch (error: unknown) {
          const code =
            error && typeof error === 'object' && 'code' in error
              ? (error as { code?: string }).code
              : undefined;
          if (code === 'auth/network-request-failed') {
            console.warn(
              'Firebase Auth token refresh failed (network or timeout). Trying account reload.',
            );
          } else {
            console.error('Firebase Auth token refresh failed:', error);
          }
          try {
            await currentUser.reload();
          } catch (reloadErr: unknown) {
            const rCode =
              reloadErr && typeof reloadErr === 'object' && 'code' in reloadErr
                ? (reloadErr as { code?: string }).code
                : undefined;
            if (rCode === 'auth/network-request-failed') {
              console.warn(
                'Firebase Auth account reload also failed (network). Refreshing Firestore profile only.',
              );
            } else {
              console.error('Firebase Auth account reload failed:', reloadErr);
            }
          }
        }
        const firestoreVerified = await fetchUserProfile(currentUser.uid);
        return !!(auth.currentUser?.emailVerified || firestoreVerified);
      } catch (unexpected: unknown) {
        console.warn('refreshUserProfile: unexpected failure', unexpected);
        return false;
      }
    };

    if (!refreshMutexRef.current) {
      refreshMutexRef.current = run()
        .catch((e: unknown) => {
          console.warn('refreshUserProfile: rejected promise', e);
          return false;
        })
        .finally(() => {
          refreshMutexRef.current = null;
        });
    }
    return refreshMutexRef.current;
  }, [auth, currentUser, fetchUserProfile]);

  useEffect(() => {
    if (!currentUser) {
      Sentry.setUser(null);
      return;
    }
    const username =
      userProfile?.firstName && userProfile?.lastName
        ? `${userProfile.firstName} ${userProfile.lastName}`
        : userProfile?.name;
    Sentry.setUser({
      id: currentUser.uid,
      email: currentUser.email ?? userProfile?.email ?? undefined,
      username: username || undefined,
    });
    if (userProfile?.isAdmin !== undefined) {
      Sentry.setTag('is_admin', userProfile.isAdmin ? 'true' : 'false');
    }
    if (userProfile?.profileComplete !== undefined) {
      Sentry.setTag('profile_complete', userProfile.profileComplete ? 'true' : 'false');
    }
    const unitCount = Array.isArray(userProfile?.units) ? userProfile.units.length : 0;
    Sentry.setTag('unit_count', String(unitCount));
  }, [currentUser, userProfile]);

  useEffect(() => {
    /**
     * If Firestore never settles (offline tab, blocked request, proxy), awaiting profile sync
     * would block forever and `finally` would never run, leaving the app on the auth spinner.
     */
    const AUTH_PROFILE_SYNC_TIMEOUT_MS = 25_000;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      let forceReleaseTimeout: number | undefined;

      try {
        setCurrentUser(user);
        if (user) {
          forceReleaseTimeout = window.setTimeout(() => {
            console.warn(
              '[Auth] Profile sync still pending after timeout; releasing loading state. Check network and Firestore.',
            );
            setLoading(false);
          }, AUTH_PROFILE_SYNC_TIMEOUT_MS);

          const docEmailVerified = await fetchUserProfile(user.uid);

          const updateData: any = { lastLoginAt: new Date() };
          if (user.emailVerified) {
            // Rules only accept emailVerified: true when the ID token says so; a restored session's
            // token can predate verification, so refresh it for the one-time transition.
            if (!docEmailVerified) {
              try {
                await user.getIdToken(true);
              } catch (tokenError) {
                console.warn('Could not refresh ID token before syncing emailVerified', tokenError);
              }
            }
            updateData.emailVerified = true;
          }

          await setDoc(doc(db, 'users', user.uid), updateData, { merge: true });

          if (user.emailVerified) {
            await fetchUserProfile(user.uid);
          }
        } else {
          setUserProfile(null);
        }
      } catch (e) {
        console.error('Auth state change handler failed:', e);
      } finally {
        if (forceReleaseTimeout !== undefined) {
          window.clearTimeout(forceReleaseTimeout);
        }
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [auth, db, fetchUserProfile]);

  const signup = async (email: string, password: string, additionalData: Record<string, any> = {}) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create user profile in Firestore. Unit memberships an officer added for this email are
      // linked after the email is verified (claimEmailMembershipsOnce).
      await setDoc(doc(db, 'users', user.uid), {
        email,
        futureMeetingsOptIn: false,
        units: [],
        createdAt: new Date(),
        lastLoginAt: new Date(),
        twoFactorEnabled: false,
        profileComplete: true,
        emailVerified: false,
        ...additionalData,
      });

      // Send verification email using Firebase's built-in method
      // NO BACKEND NEEDED - this is Firebase Auth's native feature!
      try {
        const { sendEmailVerification: firebaseSendEmailVerification } = await import('firebase/auth');
        await firebaseSendEmailVerification(user);
      } catch (emailError) {
        console.error('Error sending verification email:', emailError);
        // Don't block signup if email fails
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      if (onError) {
        if (error.code === 'auth/email-already-in-use') {
          onError('This email is already registered.');
        } else if (error.code === 'auth/weak-password') {
          onError('Password is too weak. Please use a stronger password.');
        } else {
          onError('Failed to create account. Please try again.');
        }
      }
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      if (onSuccess) {
        onSuccess('Successfully logged in!');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      if (onError) {
        if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
          onError('Invalid email or password.');
        } else if (error.code === 'auth/too-many-requests') {
          onError('Too many failed attempts. Please try again later.');
        } else {
          onError('Failed to log in. Please try again.');
        }
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
      if (onSuccess) {
        onSuccess('Successfully logged out!');
      }
    } catch (error) {
      console.error('Logout error:', error);
      if (onError) {
        onError('Failed to log out. Please try again.');
      }
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      if (onSuccess) {
        onSuccess('Password reset email sent! Please check your inbox.');
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      if (onError) {
        if (error.code === 'auth/user-not-found') {
          onError('No account found with this email address.');
        } else if (
          (typeof error.code === 'string' && error.code.includes('requests-from-referer')) ||
          (typeof error.message === 'string' && error.message.includes('requests-from-referer'))
        ) {
          onError(
            'Password reset is blocked for this site origin. In Google Cloud Console, open the Firebase Browser API key, set HTTP referrer restrictions to include this dev URL (e.g. http://localhost:3000/*), or use Firebase emulators locally (VITE_USE_EMULATORS=true).',
          );
        } else {
          onError('Failed to send password reset email. Please try again.');
        }
      }
      throw error;
    }
  };

  const sendVerificationEmail = async (redirectPath?: string) => {
    try {
      if (!currentUser) {
        if (onError) {
          onError('You must be logged in to send a verification email.');
        }
        return;
      }

      // Use Firebase's built-in email verification - NO BACKEND NEEDED!
      // This is Firebase Auth's native method - simplest possible solution
      const { sendEmailVerification: firebaseSendEmailVerification } = await import('firebase/auth');
      
      // Configure the action code settings (optional)
      const actionCodeSettings = redirectPath ? {
        url: `${window.location.origin}/verify-email?verified=true&redirect=${encodeURIComponent(redirectPath)}`,
        handleCodeInApp: false,
      } : undefined;
      
      await firebaseSendEmailVerification(currentUser, actionCodeSettings);
      
      if (onSuccess) {
        onSuccess('Verification email sent! Please check your inbox.');
      }
    } catch (error: any) {
      console.error('Send verification email error:', error);
      if (onError) {
        if (error.code === 'auth/too-many-requests') {
          onError('Too many requests. Please wait a few minutes and try again.');
        } else if (error.message) {
          onError(`Failed to send verification email: ${error.message}`);
        } else {
          onError('Failed to send verification email. Please try again.');
        }
      }
      throw error;
    }
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    refreshUserProfile,
    sendVerificationEmail, // Always available now (uses callable functions)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

